import GoogleMapsUtility from '~/utils/GoogleMapsUtility';

// Stato condiviso: traccia se l'utente ha selezionato un luogo
const userHasSelectedPlace: Record<string, boolean> = {
    personale: false,
    business: false
};

export function getUserHasSelectedPlace(type: string): boolean {
    return userHasSelectedPlace[type] ?? false;
}

export function resetUserHasSelectedPlace(type: string): void {
    userHasSelectedPlace[type] = false;
}

/**
 * Visualizza i risultati delle predizioni in una lista HTML.
 */
function displayResults(predictions: any[], type: string): void {
    const normalizedPredictions = GoogleMapsUtility.normalizePredictions(predictions);

    const results = normalizedPredictions.map(prediction => {
        const li = document.createElement('li');
        li.innerHTML = `<strong>${GoogleMapsUtility.getAddressType(prediction.type)}</strong>${prediction.label}`;
        li.addEventListener('click', () => handleSelect(prediction.value, type));
        return li;
    });

    const resultsContainer = document.getElementById(`results-${type}`);
    if (!resultsContainer) return;

    resultsContainer.innerHTML = '';
    results.forEach(result => resultsContainer.appendChild(result));

    if (results.length > 0) {
        resultsContainer.classList.add('results-open');
    } else {
        resultsContainer.classList.remove('results-open');
    }
}

/**
 * Gestisce l'input dell'utente, controlla la cache e fa la chiamata all'API se necessario.
 */
async function handleInput(
    query: string,
    type: string,
    autocompleteService: any
): Promise<void> {
    const resultsContainer = document.getElementById(`results-${type}`);
    if (!resultsContainer) return;

    const isNumeric = /^\d+$/.test(query);

    if (isNumeric) {
        if (query.length === 5) {
            // È un possibile CAP italiano → continua con la ricerca Google Maps
        } else {
            // È un Codice Annuncio → non fare ricerca
            resultsContainer.innerHTML = '';
            return;
        }
    }

    if (GoogleMapsUtility.isValidSearchQuery(query) || GoogleMapsUtility.isPossibleItalianCAP(query)) {
        const cachedResults = localStorage.getItem(query);
        if (cachedResults) {
            console.info(`%cRisultati per "${query}" recuperati dalla cache.`, 'color: green;');
            displayResults(JSON.parse(cachedResults), type);
        } else {
            console.info(`%cNuova richiesta a Google Maps API per query: "${query}"`, 'color: blue;');
            try {
                const predictions = await GoogleMapsUtility.getPlacePredictions(query, autocompleteService);
                localStorage.setItem(query, JSON.stringify(predictions));
                displayResults(predictions, type);
            } catch (error) {
                userHasSelectedPlace[type] = false;
                console.error('Error:', error);
            }
        }
    }
}

/**
 * Aggiorna i campi relativi all'indirizzo nel form.
 */
function updateAddressFields(
    fields: Record<string, HTMLInputElement | null>,
    place: any,
    replaceDotWithComma: (v: string) => string
): void {
    if (fields.indirizzo) {
        fields.indirizzo.value = place.formatted_address ?? '';
        console.info(`Indirizzo: ${fields.indirizzo.value}`);
    }

    if (fields.latitudineNW && fields.latitudineSE && fields.longitudineNW && fields.longitudineSE) {
        const bounds = place.geometry.viewport;
        fields.latitudineNW.value = replaceDotWithComma(bounds.getNorthEast().lat().toString());
        fields.longitudineNW.value = replaceDotWithComma(bounds.getNorthEast().lng().toString());
        fields.latitudineSE.value = replaceDotWithComma(bounds.getSouthWest().lat().toString());
        fields.longitudineSE.value = replaceDotWithComma(bounds.getSouthWest().lng().toString());

        console.info(`Latitudine NW: ${fields.latitudineNW.value}, Longitudine NW: ${fields.longitudineNW.value}`);
        console.info(`Latitudine SE: ${fields.latitudineSE.value}, Longitudine SE: ${fields.longitudineSE.value}`);

        if (fields.provincia) fields.provincia.value = '';
        if (fields.comune) fields.comune.value = '';
        if (fields.regione) fields.regione.value = '';
        console.info('Azzerati i campi Provincia, Comune e Regione.');
    }
}

/**
 * Aggiorna i campi relativi alla località nel form.
 */
function updateLocationFields(
    fields: Record<string, HTMLInputElement | null>,
    place: any
): void {
    if (fields.provincia) {
        fields.provincia.value = GoogleMapsUtility.normalizeProvincia(
            GoogleMapsUtility.getComponent(place.address_components, 'administrative_area_level_2')
        );
        console.info(`Provincia: ${fields.provincia.value}`);
    }
    if (fields.comune) {
        // Google Maps restituisce i comuni come 'locality' per le città grandi,
        // ma come 'administrative_area_level_3' per i comuni più piccoli (es. Moconesi).
        // Si prova prima 'locality', poi si fa fallback su 'administrative_area_level_3'.
        const comuneValue =
            GoogleMapsUtility.getComponent(place.address_components, 'locality') ||
            GoogleMapsUtility.getComponent(place.address_components, 'administrative_area_level_3');
        fields.comune.value = GoogleMapsUtility.normalizeComune(comuneValue);
        console.info(`Comune: ${fields.comune.value}`);
    }
    if (fields.regione) {
        fields.regione.value = GoogleMapsUtility.getComponent(place.address_components, 'administrative_area_level_1');
        console.info(`Regione: ${fields.regione.value}`);
    }

    if (fields.latitudineNW && fields.latitudineSE && fields.longitudineNW && fields.longitudineSE) {
        fields.latitudineNW.value = '';
        fields.latitudineSE.value = '';
        fields.longitudineNW.value = '';
        fields.longitudineSE.value = '';
        console.info('Azzerati i campi Latitudine e Longitudine.');
    }
}

/**
 * Aggiorna i campi nascosti nel form specificato.
 */
function updateHiddenFields(type: string, place: any): void {
    const form = document.getElementById(`frmcercahome-${type}`) as HTMLFormElement | null;
    if (!form) {
        console.error(`Form con id frmcercahome-${type} non trovato.`);
        return;
    }

    const fields: Record<string, HTMLInputElement | null> = {
        indirizzo: form.querySelector('[name="Indirizzo"]'),
        provincia: form.querySelector('[name="Provincia"]'),
        comune: form.querySelector('[name="Comune"]'),
        regione: form.querySelector('[name="Regione"]'),
        latitudineNW: form.querySelector('[name="LatitudineNW"]'),
        latitudineSE: form.querySelector('[name="LatitudineSE"]'),
        longitudineNW: form.querySelector('[name="LongitudineNW"]'),
        longitudineSE: form.querySelector('[name="LongitudineSE"]')
    };

    const replaceDotWithComma = (value: string) => value.replace('.', ',');

    if (GoogleMapsUtility.isAddressType(place)) {
        updateAddressFields(fields, place, replaceDotWithComma);
    } else {
        updateLocationFields(fields, place);
    }
}

/**
 * Gestisce la selezione di un risultato e aggiorna gli input nascosti.
 */
async function handleSelect(placeId: string, type: string): Promise<void> {
    console.info(`handleSelect chiamata con placeId: ${placeId} e type: ${type}`);

    userHasSelectedPlace[type] = true;

    const placeService = new (window as any).google.maps.places.PlacesService(document.createElement('div'));

    GoogleMapsUtility.getPlaceDetails(placeId, placeService)
        .then(place => {
            (document.getElementById(`autocomplete-${type}`) as HTMLInputElement).value = place.formatted_address ?? '';
            console.info(`Indirizzo formattato: ${place.formatted_address}`);

            updateHiddenFields(type, place);

            const resultsContainer = document.getElementById(`results-${type}`);
            if (resultsContainer) {
                resultsContainer.style.display = 'none';
                resultsContainer.classList.remove('results-open');
            }

            sessionStorage.setItem('formattedAddress', place.formatted_address ?? '');

            const searchButton = document.getElementById(`btn-location-immobili-${type}`);
            if (searchButton) {
                (searchButton as HTMLElement).focus();
                console.info('Focus spostato sul bottone Cerca');
            }
        })
        .catch(status => {
            console.error('Errore:', status);
        });
}

/**
 * Evidenzia l'elemento selezionato nella lista dei risultati.
 */
function highlightResult(results: HTMLCollectionOf<HTMLLIElement>, index: number, onSelect: (val: string) => void): void {
    for (let i = 0; i < results.length; i++) {
        results[i].classList.remove('highlight');
    }
    if (index >= 0) {
        results[index].classList.add('highlight');
        onSelect(results[index].innerText);
    }
}

/**
 * Inizializza il servizio di autocomplete di Google Maps per un tipo specifico.
 */
export function initAutocomplete(type: string): void {
    const input = document.getElementById(`autocomplete-${type}`) as HTMLInputElement | null;
    if (!input) return;

    const autocompleteService = new (window as any).google.maps.places.AutocompleteService();
    let debounceTimeout: ReturnType<typeof setTimeout>;
    let selectedIndex = -1;
    let selectedValue = '';

    input.addEventListener('input', function () {
        const query = (this as HTMLInputElement).value.trim().toLowerCase();
        userHasSelectedPlace[type] = false;
        const resultsContainer = document.getElementById(`results-${type}`);
        if (resultsContainer) resultsContainer.style.display = 'block';

        clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(() => handleInput(query, type, autocompleteService), 300);
    });

    input.addEventListener('keydown', function (event: KeyboardEvent) {
        const resultsContainer = document.getElementById(`results-${type}`);
        if (!resultsContainer) return;

        const results = resultsContainer.getElementsByTagName('li') as HTMLCollectionOf<HTMLLIElement>;

        if (results.length > 0) {
            switch (event.key) {
                case 'ArrowDown':
                    selectedIndex = (selectedIndex + 1) % results.length;
                    highlightResult(results, selectedIndex, val => { selectedValue = val; });
                    break;
                case 'ArrowUp':
                    selectedIndex = (selectedIndex - 1 + results.length) % results.length;
                    highlightResult(results, selectedIndex, val => { selectedValue = val; });
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (selectedIndex >= 0) {
                        input.value = selectedValue;
                        results[selectedIndex].click();
                    }
                    break;
                case 'Escape':
                    resultsContainer.style.display = 'none';
                    resultsContainer.classList.remove('results-open');
                    break;
            }
        }
    });
}
